import type { StationState } from "@station-keeping/sim";
import { create } from "zustand";
import { readItem, writeItem } from "../platform/storage";
import type { GameUiState } from "./store";

/** Le tutoriel, vu une fois par appareil (« Revoir le tutoriel » le relance depuis le panneau Station). */
const DONE_KEY = "sk.tutorial.v1";

/** Au-delà de cet âge, la station n'est plus une arrivée : pas de tutoriel d'office sur un nouvel appareil. */
export const TUTORIAL_MAX_AGE_TICKS = 30 * 60;

export type TutorialTarget =
  | "hud-score"
  | "hud-level"
  | "hud-orbit"
  | "deck-build"
  | "build-solar_panel"
  | "deck-ships"
  | "deck-failures";

export interface TutorialStep {
  id: "welcome" | "score" | "level" | "orbit" | "openBuild" | "pickSolar" | "placeSolar" | "ships" | "failures" | "offline";
  /** Élément de l'interface à encadrer (`data-tutorial`). */
  target?: TutorialTarget;
  /**
   * Étape à faire soi-même : elle passe quand la condition est remplie (pas de touche « Suivant »). La
   * condition reçoit l'état noté en entrant dans l'étape.
   */
  done?: (state: StationState, ui: GameUiState, entry: TutorialEntry) => boolean;
}

/** Ce qu'on note en entrant dans une étape (pour voir ce qui a changé depuis). */
export interface TutorialEntry {
  solarPanels: number;
}

const solarPanels = (state: StationState) => state.modules.filter((module) => module.type === "solar_panel").length;

export const TUTORIAL_STEPS: readonly TutorialStep[] = [
  { id: "welcome" },
  { id: "score", target: "hud-score" },
  { id: "level", target: "hud-level" },
  { id: "orbit", target: "hud-orbit" },
  { id: "openBuild", target: "deck-build", done: (_state, ui) => ui.panel === "build" || ui.buildType !== null },
  { id: "pickSolar", target: "build-solar_panel", done: (_state, ui) => ui.buildType === "solar_panel" },
  { id: "placeSolar", done: (state, _ui, entry) => solarPanels(state) > entry.solarPanels },
  { id: "ships", target: "deck-ships" },
  { id: "failures", target: "deck-failures" },
  { id: "offline" },
];

interface TutorialState {
  active: boolean;
  step: number;
  entry: TutorialEntry;
  start(): void;
  next(state?: StationState): void;
  finish(): void;
}

export function entryOf(state: StationState | null | undefined): TutorialEntry {
  return { solarPanels: state ? solarPanels(state) : 0 };
}

export const useTutorial = create<TutorialState>((set, get) => ({
  active: false,
  step: 0,
  entry: { solarPanels: 0 },
  start: () => set({ active: true, step: 0 }),
  next: (state) => {
    const step = get().step + 1;
    if (step >= TUTORIAL_STEPS.length) get().finish();
    else set({ step, entry: entryOf(state) });
  },
  finish: () => {
    // Navigation privée : le tutoriel reviendra, sans gravité.
    writeItem(DONE_KEY, "1");
    set({ active: false, step: 0 });
  },
}));

/** Le tutoriel se lance de lui-même à l'arrivée : station toute neuve, jamais vu sur cet appareil. */
export function shouldAutoStart(state: StationState): boolean {
  return readItem(DONE_KEY) !== "1" && state.tick < TUTORIAL_MAX_AGE_TICKS;
}
